import React, {useEffect, useMemo, useState} from "react";
import {Chart} from "primereact/chart";
import {Button} from "primereact/button";
import {Skeleton} from "primereact/skeleton";
import BoxHeader from "../../components/box-header";
import Icon from "../../../system/components/icon";
import {projectDashboardApi} from "../../../models/project/project-dashboard-api";

// «نمودار پیشرفت پروژه» in place of «منابع پروژه» on the identity tab (boss's task «تغییر هویت پروژه», 2026-10-31;
// docs/tasks/35-schedule-assistant-progress-chart.md): how far the project has come, by its milestones (outputs), day
// by day — the same rule as the project's progress figure. Without milestones it says to define them.
const AREA = "#e6511c";
const digits = (value) => new Intl.NumberFormat("fa-IR").format(value);

export function chartPoints(points) {
    const format = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {day: "numeric", month: "short"});
    return {
        labels: (points || []).map((point) => {
            try { return format.format(new Date(point.at)); } catch { return ""; }
        }),
        values: (points || []).map((point) => (point.progress === null || point.progress === undefined ? null : point.progress)),
    };
}

export default function ProjectProgressChart({projectData, onDefineMilestones}) {
    const projectKey = projectData?.documentId;
    // reload when the project's milestones change (added / archived elsewhere on the page)
    const milestoneSignature = (projectData?.deliverables || []).filter((item) => !item?.archivedAt).map((item) => item?.documentId ?? item?.id).sort().join(",");
    const [history, setHistory] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!projectKey) return undefined;
        let alive = true;
        setFailed(false);
        projectDashboardApi.milestoneProgress(projectKey)
            .then((result) => alive && setHistory(result || {points: []}))
            .catch(() => alive && setFailed(true));
        return () => { alive = false; };
    }, [projectKey, milestoneSignature]);

    const {labels, values} = useMemo(() => chartPoints(history?.points), [history]);
    const data = useMemo(() => ({
        labels,
        datasets: [{
            label: "پیشرفت پروژه",
            data: values,
            borderColor: AREA,
            backgroundColor: (context) => {
                const {ctx, chartArea} = context.chart;
                if (!chartArea) return "rgba(230, 81, 28, 0.15)";
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, "rgba(230, 81, 28, 0.32)");
                gradient.addColorStop(1, "rgba(230, 81, 28, 0.02)");
                return gradient;
            },
            fill: "origin",
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.3,
            spanGaps: true,
        }],
    }), [labels, values]);
    const options = useMemo(() => ({
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {rtl: true, callbacks: {label: (item) => ` پیشرفت ${digits(item.parsed.y)}٪`}},
        },
        scales: {
            y: {min: 0, max: 100, ticks: {stepSize: 25, callback: (value) => `${digits(value)}٪`}, grid: {color: "rgba(0,0,0,0.05)"}},
            x: {reverse: true, ticks: {maxTicksLimit: 8, autoSkip: true}, grid: {display: false}},
        },
    }), []);

    const empty = history && !(history.points || []).length;
    return <div className="flex-1 flex flex-column gap-2 pb-3" data-testid="project-progress-chart">
        <BoxHeader icon={<Icon icon="mi mi:monitoring" className="text-lg text-400"/>} title="پیشرفت پروژه" className="text-sm p-3"
                   left={history?.current !== null && history?.current !== undefined
                       ? <span className="text-xs text-600">{digits(history.doneMilestones || 0)} از {digits(history.milestones || 0)} خروجی تمام شده · <strong className="text-primary text-sm">{digits(history.current)}٪</strong></span>
                       : null}/>
        <div className="px-3">
            {failed ? <div className="text-xs text-red-500 p-3">نمودار پیشرفت بارگذاری نشد.</div>
                : !history ? <Skeleton height="12rem" className="border-round-lg"/>
                : empty ? <div className="flex flex-column align-items-center justify-content-center text-center gap-2 p-4 border-1 border-dashed border-300 border-round-lg surface-0" style={{minHeight: "12rem"}}>
                    <Icon icon="mi mi:monitoring" className="text-3xl text-400"/>
                    <strong className="text-sm text-900">هنوز پیشرفتی برای نمایش نیست</strong>
                    <span className="text-xs text-600 line-height-3">برای دیدن پیشرفت پروژه، خروجی‌ها یا مایلستون‌های پروژه را تعریف کنید.<br/>پیشرفت هر خروجی از کارهای انجام‌شدهٔ آن به دست می‌آید.</span>
                    {onDefineMilestones && <Button size="small" className="text-xs mt-1" icon="pi pi-plus" label="تعریف خروجی / مایلستون" onClick={onDefineMilestones}/>}
                </div>
                : <>
                    <div style={{height: "14rem"}}><Chart type="line" data={data} options={options} style={{height: "100%"}}/></div>
                    <p className="text-2xs text-500 m-0 mt-2">پیشرفت پروژه از خروجی‌ها (مایلستون‌ها) و کارهای انجام‌شدهٔ هرکدام محاسبه می‌شود. برای دقیق‌تر شدن آن، کارها را به خروجی‌ها وصل کنید.</p>
                </>}
        </div>
    </div>;
}
